import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Dialog, FilterGroup, Typography } from '@robocorp/components';
import { styled } from '@robocorp/theme';
import { WorkItemSearchArgs } from '~/queries';

const findFilterOptionValue = (
  filterValue: undefined | string | string[],
  option: FilterGroup['options'][number],
  permanent?: boolean,
) => {
  if (Array.isArray(filterValue)) {
    return filterValue.findIndex((value) => value === option.value) >= 0;
  }
  return filterValue === option.value || permanent;
};

const mapFilterValues = ({
  filters,
  queryArgs,
}: WorkItemSearchArgs): Record<string, undefined | string | string[]> => ({
  step: filters?.activityId,
  state: filters?.state,
  exceptionType: queryArgs?.exceptionType,
});

const mapConfirmationFilters = (
  filters: WorkItemSearchArgs = {},
  filterOptions: Record<string, FilterGroup> = {},
) => {
  const filterValues = mapFilterValues(filters);

  return Object.entries(filterOptions).reduce<Record<string, string>>(
    (acc, [key, value]) => ({
      ...acc,
      [value.label]:
        value.valueLabel ??
        value.options
          .filter((option, index) =>
            findFilterOptionValue(filterValues[key], option, value.permanent && index === 0),
          )
          .map((option) => option.label)
          .join(', '),
    }),
    {},
  );
};

const FilterContainer = styled(Box)`
  overflow: auto;
`;

const FilterValue = styled(Typography)`
  white-space: nowrap;
`;

interface Props {
  title: string;
  description: string;
  filters?: WorkItemSearchArgs;
  filterOptions?: Record<string, FilterGroup>;
  isOpen: boolean;
  isLoading?: boolean;
  onClose: () => void;
  onConfirm: () => void;
  confirmButtonText?: string;
}

export const BulkConfirmationDialog: FC<Props> = ({
  title,
  description,
  filters,
  filterOptions,
  isOpen,
  isLoading,
  onClose,
  onConfirm,
  confirmButtonText,
}) => {
  const { t } = useTranslation();

  const filterEntries = useMemo(() => {
    if (!isOpen) return [];

    return Object.entries({
      [t('search')]: filters?.queryArgs?.search ? `'${filters.queryArgs.search}'` : undefined,
      ...mapConfirmationFilters(filters, filterOptions),
    }).filter(([, value]) => !!value);
  }, [t, isOpen, filters, filterOptions]);

  return (
    <Dialog open={isOpen} onClose={onClose}>
      <Dialog.Header>
        <Dialog.Header.Title title={title} />
        <Dialog.Header.Description>{description}</Dialog.Header.Description>
      </Dialog.Header>
      {filterEntries.length > 0 && (
        <Dialog.Content>
          <FilterContainer
            display="flex"
            flexDirection="column"
            borderRadius="$12"
            p="$16"
            backgroundColor="background.subtle"
            mb="$24"
            gap={6}
          >
            {filterEntries.map(([key, value]) => (
              <Box display="flex" key={key} gap={4} flex={1}>
                <Typography color="content.subtle.light">{`${key}:`}</Typography>
                <FilterValue>{value}</FilterValue>
              </Box>
            ))}
          </FilterContainer>
        </Dialog.Content>
      )}
      <Dialog.Actions>
        <Button onClick={onConfirm} loading={isLoading} disabled={isLoading}>
          {confirmButtonText ?? t('confirm')}
        </Button>
        <Button onClick={onClose} variant="secondary">
          {t('cancel')}
        </Button>
      </Dialog.Actions>
    </Dialog>
  );
};
